import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { chatbotContact } from '../../util/api';

import css from './ContactWidget.module.css';
import treeLogo from '../../assets/images/tree.png';

const choices = [
  { label: "I'm a company looking for fractional support", value: 'company' },
  { label: "I'm a professional exploring fractional work", value: 'fractional' },
  { label: 'I have a specific question', value: 'question' },
];

const categoryLabel = value => {
  const map = {
    company: 'Company — Looking for Fractional Support',
    fractional: 'Professional — Exploring Fractional Work',
    question: 'Specific Question',
  };

  return map[value] || value;
};

const ContactIcon = () => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const SendIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const TypingIndicator = () => (
  <div className={`${css.bubble} ${css.botBubble}`} aria-label="Forteh is typing">
    <div className={css.typing}>
      <span />
      <span />
      <span />
    </div>
  </div>
);

const Chatbot = () => {
  const currentUser = useSelector(state => state?.user?.currentUser);
  const currentUserEmail = currentUser?.attributes?.email || '';

  const [isOpen, setIsOpen] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [showChoices, setShowChoices] = useState(false);
  const [step, setStep] = useState('intro');
  const [category, setCategory] = useState('');
  const [question, setQuestion] = useState('');
  const [email, setEmail] = useState(currentUserEmail);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const bodyRef = useRef(null);
  const textareaRef = useRef(null);
  const choicesTimerRef = useRef(null);

  const isDone = step === 'done';
  const canSend = !!inputValue.trim() && (step === 'question' || step === 'email') && !isSubmitting;

  useEffect(() => {
    if (isOpen && !hasStarted) {
      setHasStarted(true);
      choicesTimerRef.current = setTimeout(() => {
        setShowChoices(true);
        choicesTimerRef.current = null;
      }, 700);
    }

    return () => {
      if (!isOpen && choicesTimerRef.current) {
        clearTimeout(choicesTimerRef.current);
        choicesTimerRef.current = null;
      }
    };
  }, [hasStarted, isOpen]);

  useEffect(() => {
    if (currentUserEmail && !email) {
      setEmail(currentUserEmail);
    }
  }, [currentUserEmail, email]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [
    category,
    email,
    hasStarted,
    isSubmitting,
    question,
    showChoices,
    step,
    submittedEmail,
    submitError,
  ]);

  useEffect(() => {
    if (isOpen && textareaRef.current && (step === 'question' || step === 'email')) {
      textareaRef.current.focus();
    }
  }, [isOpen, step]);

  const autoResize = textarea => {
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 110)}px`;
  };

  const handleInputChange = e => {
    setInputValue(e.target.value);
    autoResize(e.target);
  };

  const handleChoice = choice => {
    setCategory(choice.value);
    setShowChoices(false);
    setSubmitError('');
    setStep('question');
  };

  const isEmailValid = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const handleSend = async () => {
    const text = inputValue.trim();

    if (!text || isSubmitting) {
      return;
    }

    setInputValue('');
    setSubmitError('');

    if (textareaRef.current) {
      textareaRef.current.style.height = '42px';
    }

    if (step === 'question') {
      setQuestion(text);
      setStep('email');

      if (currentUserEmail) {
        setEmail(currentUserEmail);
        setInputValue(currentUserEmail);
      }
      return;
    }

    if (step === 'email') {
      if (!isEmailValid(text)) {
        setInputValue(text);
        setSubmitError('Please enter a valid email address.');
        return;
      }

      setEmail(text);
      setSubmittedEmail(text);
      setIsSubmitting(true);

      try {
        await chatbotContact({
          category: categoryLabel(category),
          email: text,
          question,
        });
        setStep('done');
      } catch (error) {
        setInputValue(text);
        setSubmitError(
          "Hmm, something went wrong on our end. Please email us directly at raman@forteh.ca — we're sorry for the trouble."
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const inputPlaceholder = step === 'email' ? 'your@email.com' : 'Type here...';

  return (
    <div className={css.contactWidget}>
      <section
        className={`${css.panel} ${isOpen ? css.visible : ''}`}
        aria-label="Forteh contact assistant"
        aria-hidden={!isOpen}
      >
        <div className={css.header}>
          <div className={css.headerInner}>
            <img src={treeLogo} alt="Forteh" className={css.headerLogo} />
            <div>
              <div className={css.wordmark}>Forteh</div>
              <div className={css.tagline}>Fractional talent, matched to your moment.</div>
            </div>
          </div>
        </div>

        <div className={css.body} ref={bodyRef}>
          {hasStarted ? (
            <div className={`${css.bubble} ${css.botBubble}`}>
              Hi there 👋 Welcome to Forteh. How can we help today?
            </div>
          ) : null}

          {showChoices ? (
            <div className={css.choices} aria-label="Choose a topic">
              {choices.map(choice => (
                <button
                  className={css.choiceButton}
                  key={choice.value}
                  type="button"
                  onClick={() => handleChoice(choice)}
                >
                  {choice.label}
                </button>
              ))}
            </div>
          ) : null}

          {category ? (
            <div className={`${css.bubble} ${css.userBubble}`}>
              {choices.find(choice => choice.value === category)?.label}
            </div>
          ) : null}

          {category ? (
            <div className={`${css.bubble} ${css.botBubble}`}>
              Great — what's your question? Feel free to give us as much context as you'd like.
            </div>
          ) : null}

          {question ? <div className={`${css.bubble} ${css.userBubble}`}>{question}</div> : null}

          {question ? (
            <div className={`${css.bubble} ${css.botBubble}`}>
              Got it. What's the best email address for us to reach you with an answer?
            </div>
          ) : null}

          {submittedEmail ? (
            <div className={`${css.bubble} ${css.userBubble}`}>{submittedEmail}</div>
          ) : null}

          {isSubmitting ? <TypingIndicator /> : null}

          {submitError ? <div className={`${css.bubble} ${css.botBubble}`}>{submitError}</div> : null}

          {isDone ? (
            <div className={css.confirmBox} role="status">
              <strong>You're all set</strong>
              Thanks for reaching out. We'll get back to you at <em>{email}</em> within 24 hours.
              <br />
              <br />
              In the meantime, feel free to explore{' '}
              <a href="https://forteh.ca" target="_blank" rel="noopener noreferrer">
                forteh.ca
              </a>
              .
            </div>
          ) : null}
        </div>

        {!isDone ? (
          <div className={css.footer}>
            <textarea
              ref={textareaRef}
              value={inputValue}
              placeholder={inputPlaceholder}
              rows="1"
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={step !== 'question' && step !== 'email'}
              aria-label={step === 'email' ? 'Email address' : 'Message'}
            />
            <button
              className={css.sendButton}
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              aria-label="Send message"
            >
              <SendIcon />
            </button>
          </div>
        ) : null}
      </section>

      <button
        className={`${css.launcher} ${isOpen ? css.launcherOpen : ''}`}
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        title="Contact Forteh"
        aria-label={isOpen ? 'Close contact form' : 'Contact Forteh'}
        aria-expanded={isOpen}
      >
        <ContactIcon />
      </button>
    </div>
  );
};

export default Chatbot;
